import React, {Fragment, useRef} from 'react';
import {Popup, Flex, Separator, Link, Breadcrumbs, Activities, Card, Tree, Goals, Button, Layout, Icon, Diary, OpenAi} from 'components';
import {PlanSettings, PlanSorting, StartPoint} from '.';
import {returnNull, modal, map, last, useDragStart, useDrop, useNamedSelector, useParams, useGlobalDrop, openPopup, redirect, useSelector} from 'helpers';
import {attach, removeImages} from 'store/activities';
import {update} from 'store/sorting';
import {settings} from 'store';

export function Plan() {
  const container = useRef();
  const { id } = useParams();
  const { goal, editable, images, activities, milestones, diary, preferences = {} } = useNamedSelector('goals.plan', { id, context: 'goals' });
  const {gptConnects} = useSelector(settings.settings);

  useGlobalDrop(({type}) => type.match(/milestone|activity/), (event, grabbed) => update({
    all: map(grabbed.type === 'milestone' ? milestones : activities, 'id'),
    container: container.current,
    grabbed,
    modelId: id,
    name: 'MyPlan'
  }));


  return (
    <Fragment>
      <Layout.Header justified>
        <Breadcrumbs>
          <Link to="/goals/">Goals</Link>
          <Link to=".">{goal.name}</Link>
          <b>My Plan</b>
        </Breadcrumbs>
        <Icon
          name="sort"
          className="secondary pull-right"
          onClick={openPopup(PlanSorting, {goal, preferences})}
        />
        <Icon
          name="settings"
          className="secondary"
          Sub={preferences.sort.name == 'customOrder' && 'bullet'}
          onClick={openPopup(PlanSettings, {goal, preferences})}
        />
      </Layout.Header>
      <div ref={container}>
        <Card background={last(images) || goal.category} status={goal.status} is-tall>
          <Card.Toolbar className={['no-mask', 'left']}>
            { editable && <Goals.Single.CustomImage {...goal} /> }
            { images && <Button onClick={() => removeImages(images)} icon={{ name: 'edit-image', Sub: 'close' }} className={['tiny', 'secondary', 'filled']}>Remove</Button> }
          </Card.Toolbar>
          <Goals.Single.Card {...goal} ActionMenu={ActionMenu} detailed />
        </Card>
        { milestones.length == 0 &&
          <>
            <Separator className="medium" icon="openai">
              ChatGPT
              <u />
              <Button
                className={{primary: true, muted: !gptConnects}}
                onClick={() => redirect('./review/')}
                children="Ask ChatGPT"
              />
            </Separator>
            <p>Create a detailed Action Plan for this goal. </p>
            <p className="small-spacing">
              <OpenAi.Alert />
            </p>
          </>
        }
        <Separator className="medium" heading="Milestones" icon="goal">
          <u />
          { editable &&
            <Icon name="plus" onClick={modal(Goals.Milestones.EditForm, { goal })} />
          }
        </Separator>
        { milestones.length == 0 &&
          <p>The best way to achieve your long-term goal is to list all the milestones and activities that you need to complete along the way.</p>
        }
        <Tree className="spacing">
          { preferences.sort.name == 'oldest' && <Tree.Item><StartPoint {...goal} /></Tree.Item> }

          { milestones.map(milestone =>
            <Tree.Item muted={goal.status == 'Completed' || milestone.status == 'Completed'} key={milestone.id} data-id={milestone.id} {...useDragStart(editable && {type: 'milestone', id: milestone.id, parentId: milestone.parent_id})}>
              <Goals.Milestones.Card
                {...milestone}
                Checkbox={editable && Goals.Milestones.Checkbox}
                menu
              />
              { milestone.activities.length > 0 &&
                <Tree.Sub>
                  { milestone.activities.map(activity =>
                    <Activities.Card
                      Checkbox={Activities.Checkbox}
                      Status={returnNull}
                      full
                      id={activity.id}
                      key={activity.id}
                      context={{ goal }}
                      {...useDragStart({ type: 'activity', parentId: activity.goal_id, id: activity.id })}
                    />
                  ) }
                </Tree.Sub>
              }
            </Tree.Item>
          ) }

          { preferences.sort.name == 'newest' && <Tree.Item><StartPoint {...goal} /></Tree.Item> }
        </Tree>

        <div className="medium-spacing" {...useDrop(attach, ({type, parentId}) => type === 'activity' && parentId)} data-id={id}>
          <Separator className="medium" icon="clipboard">
            Activities & Routines
            <u />
            { editable && <Icon name="plus" onClick={modal(Activities.Single.EditForm, { goal })} /> }
          </Separator>

          <p>Make a list of everything you have to do to make progress towards your goal.</p>
          { activities.map(activity =>
            <Activities.Card
              Checkbox={Activities.Checkbox}
              Status={returnNull}
              data-object-type="activity"
              full
              id={activity.id}
              key={activity.id}
              context={{ goal }}
              {...useDragStart({ type: 'activity', id: activity.id })}
            />
          ) }
        </div>

        <Separator className="medium" heading="Journal" icon="diary">
          <u />
          { editable &&
            <Icon name="plus" onClick={modal(Diary.EditForm, { context: { goal } })} />
          }
        </Separator>
        <p>Use the Journal to take notes and reflect on your journey. </p>
        { diary.map(record =>
          <Diary.Card key={record.id} {...record} context={{ goal }} Status={returnNull} />
        ) }
      </div>
    </Fragment>
  );
}

const ActionMenu = ({editable, id}) => editable && (
  <Card.RightMenu className="floated">
    <Flex className="secondary">
      <Icon name="pencil" to="./edit/" />
      <Icon name="menu" onClick={openPopup(Goals.Single.Menu, {id, Wrapper: Popup.Menu})} />
    </Flex>
  </Card.RightMenu>
);
